import React, { useEffect, useState, useRef } from "react";
import AdminHeader from "../../components/header/AdminHeader";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import "../../css/ProductAddPage.css";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/auth";
import FacebookIcon from "@mui/icons-material/Facebook";
import axios from "axios";
import FacebookPostModal from "../../components/FacebookPostModal";

const ProductViewPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [auth] = useAuth();

    // "Post to Facebook"
    const [facebookProduct, setFacebookProduct] = useState(null);
    const [showFacebookModal, setShowFacebookModal] = useState(false);
    const [facebookStatus, setFacebookStatus] = useState(null);
    // Posts made this visit, by product id. Kept apart from the product arrays so
    // the filter/infinite-scroll effects don't re-run and jump the list back to the top.
    const [postedThisVisit, setPostedThisVisit] = useState({});
    const facebookInfoFor = (product) => postedThisVisit[product._id] || product.facebook;

    // Get URL parameters
    const searchParams = new URLSearchParams(location.search);
    const urlSearch = searchParams.get('search') || '';
    const urlCategory = searchParams.get('category') || '';
    const urlSubcategory = searchParams.get('subcategory') || '';

    const [allProducts, setAllProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(urlCategory);
    const [selectedSubcategory, setSelectedSubcategory] = useState(urlSubcategory);
    const [searchQuery, setSearchQuery] = useState(urlSearch);
    const [isLoading, setIsLoading] = useState(true);

    const topRef = useRef(null);
    const [displayedProducts, setDisplayedProducts] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const itemsPerLoad = 24;
    const observerTarget = useRef(null);

    const apiUrl = `${process.env.REACT_APP_API}`;

    // Fetch all data on mount
    useEffect(() => {
        getAllData();
        getCategories();
    }, []);

    // Is the shop's Facebook Page connected? (shown as a small banner)
    useEffect(() => {
        if (!auth.token) return;
        axios
            .get(`${apiUrl}/facebook/status`)
            .then(({ data }) => setFacebookStatus(data))
            .catch(() => setFacebookStatus(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.token]);

    // After a post goes out, remember it on the product card
    const handleFacebookPosted = (productId, facebookInfo) => {
        setPostedThisVisit((prev) => ({ ...prev, [productId]: facebookInfo }));
    };

    const openFacebookModal = (product) => {
        setFacebookProduct(product);
        setShowFacebookModal(true);
    };

    // Load subcategories if URL has a category
    useEffect(() => {
        if (urlCategory) {
            getSubcategories(urlCategory);
        }
    }, [urlCategory]);

    // Restore scroll position when returning from edit page or after actions
    useEffect(() => {
        if (filteredProducts.length > 0 && !isLoading) {
            const timer = setTimeout(() => {
                const savedScroll = sessionStorage.getItem('productPageScroll');
                const savedDisplayCount = sessionStorage.getItem('productDisplayCount');

                if (savedScroll) {
                    // Restore the number of displayed products first
                    if (savedDisplayCount) {
                        const count = parseInt(savedDisplayCount);
                        setDisplayedProducts(filteredProducts.slice(0, count));
                        setHasMore(count < filteredProducts.length);
                    }

                    // Then restore scroll position
                    requestAnimationFrame(() => {
                        window.scrollTo(0, parseInt(savedScroll));
                    });

                    // Clear the saved values
                    sessionStorage.removeItem('productPageScroll');
                    sessionStorage.removeItem('productDisplayCount');
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [filteredProducts.length, isLoading]);

    // Save scroll position periodically while user is browsing
    useEffect(() => {
        const handleScroll = () => {
            sessionStorage.setItem('productPageScrollTemp', window.scrollY.toString());
            sessionStorage.setItem('productDisplayCountTemp', displayedProducts.length.toString());
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [displayedProducts.length]);

    const getCategories = async () => {
        try {
            const res = await fetch(`${apiUrl}/categories`, {
                method: "GET",
            });
            const data = await res.json();
            setCategories(data);
        } catch (err) {
            console.error("Error fetching categories:", err);
        }
    };

    const getSubcategories = async (categoryName) => {
        try {
            const res = await fetch(
                `${apiUrl}/categories/${categoryName}/subcategories`,
                {
                    method: "GET",
                }
            );
            const data = await res.json();
            setSubcategories(data.subCategories || []);
        } catch (err) {
            console.error("Error fetching subcategories:", err);
        }
    };

    const getAllData = async () => {
        try {
            setIsLoading(true);
            const res = await fetch(`${apiUrl}/products`, {
                method: "GET",
            });
            const data = await res.json();
            console.log("Products fetched:", data.length);
            setAllProducts(data);
            setFilteredProducts(data);
            setIsLoading(false);
        } catch (err) {
            console.error("Error fetching products:", err);
            setIsLoading(false);
            toast.error("Error loading products");
        }
    };

    // Update URL with current filter state
    const updateURL = (search, category, subcategory) => {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (category) params.set('category', category);
        if (subcategory) params.set('subcategory', subcategory);

        const newUrl = params.toString() ? `?${params.toString()}` : '';
        navigate(`${location.pathname}${newUrl}`, { replace: true });
    };

    // Filter products based on search and category/subcategory
    useEffect(() => {
        let filtered = [...allProducts];

        // Apply search filter
        if (searchQuery.trim() !== "") {
            filtered = filtered.filter((product) =>
                product.productName.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        // Apply category filter
        if (selectedCategory) {
            filtered = filtered.filter((product) => {
                const categorySlug = product.productCategory && product.productCategory.slug;
                return categorySlug === selectedCategory;
            });
        }

        // Apply subcategory filter
        if (selectedSubcategory) {
            filtered = filtered.filter((product) => {
                const subCategorySlug = product.subCategory && product.subCategory.slug;
                return subCategorySlug === selectedSubcategory;
            });
        }

        setFilteredProducts(filtered);

        // Check if we're restoring state
        const savedDisplayCount = sessionStorage.getItem('productDisplayCount');
        if (savedDisplayCount) {
            // Keep the saved count during restoration
            const count = parseInt(savedDisplayCount);
            setDisplayedProducts(filtered.slice(0, count));
            setHasMore(count < filtered.length);
        } else {
            // Normal reset - load initial batch
            setDisplayedProducts(filtered.slice(0, itemsPerLoad));
            setHasMore(filtered.length > itemsPerLoad);
        }
    }, [searchQuery, selectedCategory, selectedSubcategory, allProducts]);

    // Load more products for infinite scroll
    const loadMoreProducts = () => {
        const currentLength = displayedProducts.length;
        const nextProducts = filteredProducts.slice(currentLength, currentLength + itemsPerLoad);

        setDisplayedProducts([...displayedProducts, ...nextProducts]);
        setHasMore(currentLength + nextProducts.length < filteredProducts.length);
    };

    // Intersection Observer for infinite scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isLoading) {
                    loadMoreProducts();
                }
            },
            { threshold: 0.1 }
        );

        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [hasMore, displayedProducts, filteredProducts, isLoading]);

    const handleCategoryChange = async (e) => {
        const categoryName = e.target.value;
        setSelectedCategory(categoryName);
        setSelectedSubcategory("");
        updateURL(searchQuery, categoryName, "");

        if (categoryName) {
            getSubcategories(categoryName);
        } else {
            setSubcategories([]);
        }
    };

    const handleSubcategoryChange = (e) => {
        const subcategoryName = e.target.value;
        setSelectedSubcategory(subcategoryName);
        updateURL(searchQuery, selectedCategory, subcategoryName);
    };

    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        updateURL(query, selectedCategory, selectedSubcategory);
    };

    const clearFilters = () => {
        setSearchQuery("");
        setSelectedCategory("");
        setSelectedSubcategory("");
        setSubcategories([]);
        setFilteredProducts(allProducts);
        updateURL("", "", "");

        // Clear saved scroll positions
        sessionStorage.removeItem('productPageScroll');
        sessionStorage.removeItem('productDisplayCount');
        sessionStorage.removeItem('productPageScrollTemp');
        sessionStorage.removeItem('productDisplayCountTemp');
    };

    const saveScrollPosition = () => {
        // Use temp values if available, otherwise use current scroll
        const scrollPos = sessionStorage.getItem('productPageScrollTemp') || window.scrollY.toString();
        const displayCount = sessionStorage.getItem('productDisplayCountTemp') || displayedProducts.length.toString();

        sessionStorage.setItem('productPageScroll', scrollPos);
        sessionStorage.setItem('productDisplayCount', displayCount);
    };

    const deleteProduct = async (id, productName) => {
        const confirmDelete = window.confirm(
            `Are you sure you want to delete "${productName}"?`
        );

        if (confirmDelete) {
            try {
                // Save scroll position before deleting
                saveScrollPosition();

                const res = await fetch(`${apiUrl}/product/${id}`, {
                    method: "DELETE",
                    headers: { Authorization: auth.token },
                });

                const deleteProduct = await res.json();

                if (!res.ok || !deleteProduct) {
                    toast.error("Error deleting product");
                    // Clear saved position on error
                    sessionStorage.removeItem('productPageScroll');
                    sessionStorage.removeItem('productDisplayCount');
                } else {
                    toast.success("Product Deleted!");
                    // Reload data - scroll position will be restored by useEffect
                    await getAllData();
                }
            } catch (err) {
                console.error("Error deleting product:", err);
                toast.error("Error deleting product");
                // Clear saved position on error
                sessionStorage.removeItem('productPageScroll');
                sessionStorage.removeItem('productDisplayCount');
            }
        }
    };

    return (
        <>
            <AdminHeader />
            <div ref={topRef}></div>
            <div className="container mt-4" style={{ maxWidth: "1400px" }}>
                <h2 className="mb-4">Manage Products</h2>

                {facebookStatus && !facebookStatus.configured && (
                    <div className="alert alert-warning py-2 d-flex align-items-center gap-2">
                        <FacebookIcon fontSize="small" />
                        <span>
                            Facebook isn't connected yet, so "Post to Facebook" won't work until it is.
                            See <code>docs/FACEBOOK_SETUP.md</code>.
                        </span>
                    </div>
                )}
                {facebookStatus && facebookStatus.configured && facebookStatus.error && (
                    <div className="alert alert-danger py-2 d-flex align-items-center gap-2">
                        <FacebookIcon fontSize="small" />
                        <span>Facebook connection problem: {facebookStatus.error}</span>
                    </div>
                )}
                {facebookStatus && facebookStatus.page && (
                    <p className="text-muted small d-flex align-items-center gap-1">
                        <FacebookIcon fontSize="small" style={{ color: "#1877F2" }} />
                        Posting as{" "}
                        <a href={facebookStatus.page.link} target="_blank" rel="noopener noreferrer">
                            {facebookStatus.page.name}
                        </a>
                    </p>
                )}

                {/* Search and Filter Section */}
                <div className="filter-section mb-4 p-4 bg-light rounded">
                    {/* Search Bar */}
                    <div className="row mb-3">
                        <div className="col-md-8">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <SearchIcon />
                                </span>
                                <input
                                    type="text"
                                    className="form-control form-control-lg"
                                    placeholder="Search by product name..."
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                />
                                {searchQuery && (
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => setSearchQuery("")}
                                    >
                                        <ClearIcon />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="col-md-4 d-flex align-items-center">
                            <button
                                className="btn btn-outline-primary w-100"
                                onClick={clearFilters}
                            >
                                Clear All Filters
                            </button>
                        </div>
                    </div>

                    {/* Category and Subcategory Filters */}
                    <div className="row">
                        <div className="col-md-6">
                            <label htmlFor="category" className="form-label fw-bold">
                                Filter by Category
                            </label>
                            <select
                                className="form-select"
                                id="category"
                                onChange={handleCategoryChange}
                                value={selectedCategory}
                            >
                                <option value="">All Categories</option>
                                {categories.map((category) => (
                                    <option key={category.name} value={category.slug}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {selectedCategory && (
                            <div className="col-md-6">
                                <label htmlFor="subcategory" className="form-label fw-bold">
                                    Filter by Subcategory
                                </label>
                                <select
                                    className="form-select"
                                    id="subcategory"
                                    onChange={handleSubcategoryChange}
                                    value={selectedSubcategory}
                                >
                                    <option value="">All Subcategories</option>
                                    {subcategories.map((subcategory) => (
                                        <option key={subcategory.name} value={subcategory.slug}>
                                            {subcategory.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Results Count */}
                    <div className="mt-3">
                        <p className="text-muted mb-0">
                            Showing {displayedProducts.length} of {filteredProducts.length}{" "}
                            products
                            {(searchQuery || selectedCategory || selectedSubcategory) && (
                                <span className="text-primary"> (filtered)</span>
                            )}
                        </p>
                    </div>
                </div>

                {/* Loading State */}
                {isLoading && (
                    <div className="text-center my-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Loading products...</p>
                    </div>
                )}

                {/* No Results */}
                {!isLoading && filteredProducts.length === 0 && (
                    <div className="text-center my-5">
                        <h4>No products found</h4>
                        <p className="text-muted">
                            Try adjusting your search or filters
                        </p>
                        <button className="btn btn-primary" onClick={clearFilters}>
                            Clear Filters
                        </button>
                    </div>
                )}

                {/* Products Grid */}
                {!isLoading && filteredProducts.length > 0 && (
                    <>
                        <div className="row g-4">
                            {displayedProducts.map((product) => {
                                const frontImage = product.images && product.images.front;
                                return (
                                    <div key={product._id} className="col-6 col-md-6 col-lg-4 col-xl-3">
                                        <div className="card h-100 shadow-sm product-card">
                                            <div className="position-relative">
                                                <img
                                                    src={frontImage}
                                                    className="card-img-top"
                                                    alt={product.productName}
                                                    style={{
                                                        height: "250px",
                                                        objectFit: "cover",
                                                        objectPosition: "center",
                                                    }}
                                                    loading="lazy"
                                                />
                                            </div>
                                            <div className="card-body d-flex flex-column">
                                                <h6 className="card-title text-truncate" title={product.productName}>
                                                    {product.productName}
                                                </h6>
                                                <div className="mt-auto">
                                                    <div className="d-flex gap-2 mt-3">
                                                        <NavLink
                                                            to={`/admin/product/update/${product._id}`}
                                                            onClick={saveScrollPosition}
                                                            className="btn btn-primary btn-sm flex-grow-1"
                                                        >
                                                            <EditIcon fontSize="small" /> Edit
                                                        </NavLink>
                                                        <button
                                                            className="btn btn-danger btn-sm flex-grow-1"
                                                            onClick={() =>
                                                                deleteProduct(product._id, product.productName)
                                                            }
                                                        >
                                                            <DeleteIcon fontSize="small" /> Delete
                                                        </button>
                                                    </div>
                                                    {(() => {
                                                        const fb = facebookInfoFor(product);
                                                        const posted = Boolean(fb && fb.postId);
                                                        const postedOn = posted && fb.postedAt && !isNaN(new Date(fb.postedAt))
                                                            ? ` on ${new Date(fb.postedAt).toLocaleDateString()}`
                                                            : "";
                                                        return (
                                                            <button
                                                                className={`btn btn-sm w-100 mt-2 ${
                                                                    posted ? "btn-outline-success" : "btn-outline-primary"
                                                                }`}
                                                                onClick={() => openFacebookModal(product)}
                                                                title={
                                                                    posted
                                                                        ? `Posted${postedOn}`
                                                                        : "Share this product on the shop's Facebook Page"
                                                                }
                                                            >
                                                                <FacebookIcon fontSize="small" />{" "}
                                                                {posted ? "Posted ✓ · Post again" : "Post to Facebook"}
                                                            </button>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Infinite Scroll Observer */}
                        <div ref={observerTarget} className="text-center my-4">
                            {hasMore && (
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading more...</span>
                                </div>
                            )}
                            {!hasMore && displayedProducts.length > 0 && (
                                <p className="text-muted">
                                    ✓ All {filteredProducts.length} products loaded
                                </p>
                            )}
                        </div>
                    </>
                )}
            </div>

            {facebookProduct && (
                <FacebookPostModal
                    product={facebookProduct}
                    show={showFacebookModal}
                    onClose={() => setShowFacebookModal(false)}
                    onExited={() => setFacebookProduct(null)}
                    onPosted={handleFacebookPosted}
                />
            )}

            <style jsx>{`
                .filter-section {
                    background-color: #f8f9fa !important;
                    border: 1px solid #dee2e6;
                }

                .product-card {
                    transition: transform 0.2s, box-shadow 0.2s;
                    border: 1px solid #dee2e6;
                }

                .product-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
                }

                .card-title {
                    font-weight: 600;
                    color: #333;
                    min-height: 40px;
                }

                .input-group-text {
                    background-color: white;
                }

                .btn-sm {
                    padding: 0.375rem 0.75rem;
                    font-size: 0.875rem;
                }
            `}</style>
        </>
    );
};

export default ProductViewPage;